/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { axios as appAxios, handleAxiosClientError } from '@/libs/axios';
import { success, error } from '@/libs/notification';
import router from '@/router';
import {
  CarSecurityDeviceState,
  CarSecurityDeviceDetailData,
  CarSecurityDevicePutData,
  CarSecurityDevicePrepareData,
  CarSecurityDeviceVerifyData,
  CarSecurityDeviceLinkData
} from '@/types/carSecurityDevice';
import _ from 'lodash';
import { ElLoading } from 'element-plus';
import { handleAuthorizationError } from '@/libs/apiErrorHandler';
import { getAccessToken } from '@/libs/firebaseAuth';
import { getBaseUrl } from '@/libs/env';
import { mspfCreateDeviceErrorWhitelist } from '@/libs/mspfCreateDeviceError';

export default {
  namespaced: true,
  state: {
    detailData: null,
    putData: null,
    status: 0
  },
  mutations: {
    setDetail(
      state: CarSecurityDeviceState,
      payload: CarSecurityDeviceDetailData
    ) {
      state.detailData = payload;
    },
    setPut(state: CarSecurityDeviceState, payload: CarSecurityDevicePutData) {
      state.putData = payload;
    },
    setStatus(state: CarSecurityDeviceState, payload: number) {
      state.status = payload;
    }
  },
  actions: {
    async fetch(context: any, deviceId: any) {
      await axios
        .get(getBaseUrl() + '/carsec/device/' + deviceId, {
          headers: { auth: await getAccessToken() }
        })
        .then((response) => {
          context.commit('setDetail', response.data);
          const putData = _.cloneDeep(response.data);
          putData['id'] = deviceId;
          delete putData.financeId;
          delete putData.shipId;
          delete putData.orderCompanyName;
          delete putData.updateUserName;
          delete putData.updatedAt;
          context.commit('setPut', putData);
          context.commit('setStatus', response.data.status);
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status === 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push(`/carSecurity/device/3`);
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push(`/carSecurity/device/3`);
          } else {
            router.push('/error/server');
          }
        });
    },
    async put(context: any) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .put(
          getBaseUrl() + '/carsec/device/' + context.state.putData.id,
          {
            deviceId: context.state.putData.deviceId,
            deviceCode: context.state.putData.deviceCode,
            deviceType: context.state.putData.deviceType,
            serialCode: context.state.putData.serialCode,
            firmVersion: context.state.putData.firmVersion,
            mboot: context.state.putData.mboot,
            internalBatteryVoltage:
              context.state.putData.internalBatteryVoltage,
            orderCompanyId: context.state.putData.orderCompanyId,
            note: context.state.putData.note,
            handlingType: context.state.putData.handlingType,
            autobacsOrderNumber: context.state.putData.autobacsOrderNumber,
            deviceMinorVersion: context.state.putData.deviceMinorVersion,
            itemId: context.state.putData.itemId,
            status: context.state.putData.status
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', '端末情報の変更に成功しました。');
          router.push(`/carSecurity/device/detail/${context.state.putData.id}`);
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push(
              `/carSecurity/device/${context.state.putData.deviceType}`
            );
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async delete(context: any, userId: string) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .delete(getBaseUrl() + '/carsec/device/' + userId, {
          headers: { auth: await getAccessToken() }
        })
        .then(() => {
          success('Success', '端末情報の削除に成功しました。');
          router.push(
            `/carSecurity/device/${context.state.putData.deviceType}`
          );
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          }
          if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            return router.push(
              `/carSecurity/device/${context.state.putData.deviceType}`
            );
          }
          if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async prepare(context: any, payload: CarSecurityDevicePrepareData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .patch(
          getBaseUrl() + '/carsec/device/prepare/' + payload.id,
          {
            serialCode: payload.serialCode,
            deviceCode: payload.deviceCode,
            deviceType: payload.deviceType,
            note: payload.note,
            deviceMinorVersion: payload.deviceMinorVersion,
            itemId: payload.itemId
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', 'ステータスを準備中に変更しました。');
          loadingInstance.close();
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push(`/carSecurity/device/${payload.deviceType}`);
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async verify(context: any, payload: CarSecurityDeviceVerifyData) {
      await axios
        .patch(
          getBaseUrl() + '/carsec/device/verify/' + payload.id,
          {
            firmVersion: payload.firmVersion,
            mboot: payload.mboot,
            internalBatteryVoltage: payload.internalBatteryVoltage
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', 'ステータスを検証済に変更しました。');
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push(`/carSecurity/device/3`);
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async stop(context: any, deviceId: string) {
      await axios
        .patch(
          getBaseUrl() + '/carsec/device/stop/' + deviceId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', 'ステータスを使用中止に変更しました。');
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push('/carSecurity/device/3');
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async restart(context: any, deviceId: string) {
      await axios
        .patch(
          getBaseUrl() + '/carsec/device/restart/' + deviceId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', 'ステータスを検証済に変更しました。');
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
            router.push('/carSecurity/device/3');
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async unlink(context: any, deviceId: string) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/carsec/device/unlink/' + deviceId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', '対象端末のリンク解除に成功しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の端末情報が存在しません。');
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async link(context: any, payload: CarSecurityDeviceLinkData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      try {
        await appAxios.post('/carsec/device/link/' + payload.id, {
          autobacsOrderNumber: payload.autobacsOrderNumber
        });
        success('Success', '対象端末のリンクに成功しました。');
      } catch (e: any) {
        const status = e.response?.status;
        if (status == 404) {
          error(e.response.status, '対象の端末情報が存在しません。');
        } else if (status != null && status < 500 && status !== 401) {
          await handleAxiosClientError(e, {
            whitelistPrefixes: mspfCreateDeviceErrorWhitelist
          });
        }
      } finally {
        loadingInstance.close();
      }
    },
    async exchange(
      context: any,
      payload: {
        deviceId: string;
        sim: string;
      }
    ) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/carsec/device/sim/exchange/' + payload.deviceId,
          {
            SIMNo: payload.sim
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then((response) => {
          success('Success', 'sim情報切り替えに成功しました');
          router.push(
            '/carSecurity/device/detail/' + response.data.deviceLedgerId
          );
          loadingInstance.close();
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, e.response.data);
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async setPutData(context: any, payload: CarSecurityDevicePutData) {
      context.commit('setPut', payload);
    }
  }
};
